import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, addDoc, query, where, getDocs, orderBy, serverTimestamp, onSnapshot, Timestamp, doc, getDoc 
} from 'firebase/firestore';
import { 
  Printer, Filter, Search, Calendar, UserCheck, AlertCircle, Clock, Save, PenTool, Layout, CheckCircle 
} from 'lucide-react';

const TeacherAttendanceReport = ({ db, schoolId, setMessage, setError }) => {
  // --- ÉTATS GLOBAUX ---
  const [activeTab, setActiveTab] = useState('input'); // 'input' ou 'report'
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [schoolInfo, setSchoolInfo] = useState(null); // Nouvel état pour les infos de l'école

  // --- ÉTATS POUR L'ONGLET "SAISIE" (INPUT) ---
  const [inputDate, setInputDate] = useState(new Date().toISOString().split('T')[0]);
  const [inputTeacher, setInputTeacher] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [status, setStatus] = useState('present');
  const [comments, setComments] = useState('');
  const [todaysRecords, setTodaysRecords] = useState([]); // Aperçu du jour
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- ÉTATS POUR L'ONGLET "RAPPORT" (REPORT) ---
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterTeacher, setFilterTeacher] = useState('');
  const [attendanceReportData, setAttendanceReportData] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // 1. CHARGEMENT INITIAL DES ENSEIGNANTS
  // 2. CHARGEMENT DES SAISIES DU JOUR (ONGLET SAISIE) - Coût minimisé (getDocs)
useEffect(() => {
    if (!schoolId || !inputDate) return;
    
    // Le chargement se fait via la fonction utilitaire (lecture unique)
    fetchTodaysRecords(inputDate);
    
    // Pas de nettoyage nécessaire (pas de listener onSnapshot)
}, [db, schoolId, inputDate, setError]);

  // Chargement des informations de l'école
  useEffect(() => {
    if (!schoolId) return;
    const fetchSchoolInfo = async () => {
      try {
        const docRef = doc(db, 'schools', schoolId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSchoolInfo(docSnap.data());
        } else {
          console.warn("No school document found for ID:", schoolId);
          if (setError) setError("Informations de l'école introuvables.");
        }
      } catch (err) {
        console.error("Error fetching school info:", err);
        if (setError) setError("Erreur lors du chargement des informations de l'école.");
      }
    };
    fetchSchoolInfo();
  }, [db, schoolId, setError]);


  // 2. LISTENER POUR LES SAISIES DU JOUR (ONGLET SAISIE)
  const fetchTodaysRecords = async (dateString) => {
    if (!db || !schoolId || !dateString) return;

    // Définir la plage de la journée sélectionnée
    const start = new Date(dateString); start.setHours(0, 0, 0, 0);
    const end = new Date(dateString); end.setHours(23, 59, 59, 999);
    
    try {
        const q = query(
            collection(db, 'schools', schoolId, 'teacherAttendance'),
            where('date', '>=', start),
            where('date', '<=', end),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        const records = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTodaysRecords(records);
    } catch (error) {
        console.error("Erreur chargement saisies du jour:", error);
        if (setError) setError("Impossible de charger les enregistrements du jour.");
    }
};

  // --- LOGIQUE DE SAISIE (INPUT) ---
const handleInputSubmit = async (e) => {
    e.preventDefault();
    if (!inputTeacher) {
        if(setError) setError("Veuillez sélectionner un enseignant.");
        return;
    }
    if (!schoolId) {
        if(setError) setError("L'ID de l'école est manquant. Impossible d'enregistrer.");
        return;
    }
    setIsSubmitting(true);
    try {
        const recordDate = new Date(inputDate);
        const now = new Date();
        if (inputDate === now.toISOString().split('T')[0]) {
            recordDate.setHours(now.getHours(), now.getMinutes());
        } else {
            recordDate.setHours(12, 0);
        }

        await addDoc(collection(db, 'schools', schoolId, 'teacherAttendance'), {
            schoolId: schoolId,
            teacherId: inputTeacher,
            teacherName: teachers.find(t => t.id === inputTeacher)?.name || 'Inconnu',
            date: recordDate,
            arrivalTime: arrivalTime || null,
            departureTime: departureTime || null,
            status: status ?? null,
            comments: comments ?? null,
            createdAt: serverTimestamp(),
        });

        if(setMessage) setMessage({ type: 'success', text: "Présence enregistrée !" });
        
        // >>> MISE À JOUR MANUELLE DE LA LISTE APRÈS ENREGISTREMENT <<<
        await fetchTodaysRecords(inputDate);
        
        // Reset partiel du formulaire
        setInputTeacher('');
        setArrivalTime('');
        setDepartureTime('');
        setComments('');
    } catch (err) {
        console.error(err);
        if(setError) setError("Erreur lors de l'enregistrement.");
    } finally {
        setIsSubmitting(false);
    }
};

  // --- LOGIQUE DE RAPPORT (REPORT) ---
  const handleReportSearch = async () => {
    setIsSearching(true);
    setAttendanceReportData([]);
    try {
      const start = new Date(startDate); start.setHours(0, 0, 0, 0);
      const end = new Date(endDate); end.setHours(23, 59, 59, 999);

      let q = query(
        collection(db, 'schools', schoolId, 'teacherAttendance'),
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end)),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filtrage local par enseignant si sélectionné
      if (filterTeacher) {
        data = data.filter(rec => rec.teacherId === filterTeacher);
      }
      
      // Enrichissement des noms (au cas où le nom a changé ou n'était pas stocké)
      const enrichedData = data.map(rec => {
          // Priorité au nom stocké, sinon lookup dans la liste actuelle
          const currentName = teachers.find(t => t.id === rec.teacherId)?.name;
          return { ...rec, teacherName: rec.teacherName || currentName || 'Inconnu' };
      });

      setAttendanceReportData(enrichedData);
    } catch (err) {
      console.error(err);
      if(setError) setError("Erreur chargement rapport (Vérifiez les index Firestore).");
    } finally {
      setIsSearching(false);
    }
  };

  // Fonction pour générer le rapport HTML et l'imprimer
  const handleGeneratePrintReport = () => {
    if (!schoolInfo || attendanceReportData.length === 0) {
      if (setError) setError("Aucune donnée ou information de l'école pour générer le rapport.");
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      if (setError) setError("Impossible d'ouvrir la fenêtre d'impression. Veuillez autoriser les pop-ups.");
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Rapport de Présence Enseignants - ${schoolInfo?.name || 'École'}</title>
          <style>
              body { font-family: 'Arial', sans-serif; margin: 0; padding: 20px; color: #333; font-size: 10pt; }
              .container { max-width: 800px; margin: 0 auto; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }
              .header img { max-width: 100px; height: auto; margin-bottom: 10px; }
              .header h1 { font-size: 24px; color: #000; margin: 0; }
              .header h2 { font-size: 18px; color: #555; margin: 5px 0 0; }
              .header p { font-size: 12px; margin: 2px 0; color: #666; }
              .report-title { text-align: center; margin-bottom: 20px; }
              .report-title h3 { font-size: 20px; font-weight: bold; margin: 0; }
              .report-title p { font-size: 14px; color: #555; margin: 5px 0 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              .status-present { background-color: #d4edda; color: #155724; padding: 4px 8px; border-radius: 12px; font-size: 10px; font-weight: bold; text-align: center; display: inline-block; min-width: 60px;}
              .status-retard { background-color: #fff3cd; color: #856404; padding: 4px 8px; border-radius: 12px; font-size: 10px; font-weight: bold; text-align: center; display: inline-block; min-width: 60px;}
              .status-absent { background-color: #f8d7da; color: #721c24; padding: 4px 8px; border-radius: 12px; font-size: 10px; font-weight: bold; text-align: center; display: inline-block; min-width: 60px;}
              .emargement-line { border-bottom: 1px dashed #ccc; width: 80%; margin: 0 auto; height: 1em; display: block;}
              .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; display: flex; justify-content: space-around; font-size: 12px; text-align: center; }
              .footer div { width: 30%; }
              .footer .signature-line { border-top: 1px solid #000; margin-top: 15px; padding-top: 5px; }

              @media print {
                  body { margin: 0; padding: 0; }
                  .container { width: 100%; margin: 0; }
                  /* Assure que les styles sont appliqués à l'impression */
                  .status-present, .status-retard, .status-absent {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
              }
          </style>
      </head>
      <body>
          <div class="container">
              <div class="header">
                  ${schoolInfo?.logoUrl ? `<img src="${schoolInfo.logoUrl}" alt="Logo École">` : ''}
                  <h1>${schoolInfo?.name || 'Nom de l\'École'}</h1>
                  <h2>${schoolInfo?.adresse_ecole || ''}</h2>
                  <p>Tel: ${schoolInfo?.telephone_ecole || ''} | Email: ${schoolInfo?.email_ecole || ''}</p>
              </div>

              <div class="report-title">
                  <h3>Fiche de Présence Enseignants</h3>
                  <p>Période : ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}</p>
                  ${filterTeacher ? `<p>Enseignant : ${teachers.find(t => t.id === filterTeacher)?.name || 'Tous'}</p>` : ''}
              </div>

              <table>
                  <thead>
                      <tr>
                          <th>Date</th>
                          <th>Nom</th>
                          <th style="text-align: center;">Statut</th>
                          <th style="text-align: center;">Arrivée</th>
                          <th style="text-align: center;">Départ</th>
                          <th>Observations</th>
                          <th style="text-align: center; width: 100px;">Émargement</th>
                      </tr>
                  </thead>
                  <tbody>
                      ${attendanceReportData.length > 0 ? attendanceReportData.map((row) => {
                          const rowDate = row.date?.toDate ? row.date.toDate() : new Date(row.date);
                          const statusClass = row.status === 'present' ? 'status-present' :
                                              row.status === 'retard' ? 'status-retard' : 'status-absent';
                          return `
                              <tr>
                                  <td>${rowDate.toLocaleDateString()}</td>
                                  <td>${row.teacherName}</td>
                                  <td style="text-align: center;"><span class="${statusClass}">${row.status.toUpperCase()}</span></td>
                                  <td style="text-align: center;">${row.arrivalTime || '-'}</td>
                                  <td style="text-align: center;">${row.departureTime || '-'}</td>
                                  <td>${row.comments || ''}</td>
                                  <td style="text-align: center;"><span class="emargement-line"></span></td>
                              </tr>
                          `;
                      }).join('') : `
                          <tr>
                              <td colspan="7" style="text-align: center; padding: 40px; font-style: italic; color: #777;">Aucune donnée pour cette période.</td>
                          </tr>
                      `}
                  </tbody>
              </table>

              <div class="footer">
                  <div>
                      <p>L'Enseignant</p>
                      <div class="signature-line"></div>
                  </div>
                  <div>
                      <p>Le Surveillant</p>
                      <div class="signature-line"></div>
                  </div>
                  <div>
                      <p>Le Directeur</p>
                      <div class="signature-line"></div>
                  </div>
              </div>
          </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      
      {/* --- EN-TÊTE ET ONGLETS (Non imprimés) --- */}
      <div className="no-print mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
             <Layout className="mr-3 text-blue-600" /> Gestion Présences Enseignants
          </h2>
        </div>
        <div className="flex space-x-2 mt-4 md:mt-0 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('input')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'input' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            <PenTool size={16} className="mr-2" /> Saisie
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'report' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Printer size={16} className="mr-2" /> Rapports
          </button>
        </div>
      </div>

      {/* ================= CONTENU ONGLET : SAISIE ================= */}
      {activeTab === 'input' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Formulaire de gauche */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-700">
              <Clock className="mr-2" size={20}/> Nouvelle Entrée
            </h3>
            <form onSubmit={handleInputSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <input type="date" required value={inputDate} onChange={e => setInputDate(e.target.value)} 
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Enseignant</label>
                        <select required value={inputTeacher} onChange={e => setInputTeacher(e.target.value)}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500">
                            <option value="">-- Sélectionner --</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <div className="flex space-x-4 mt-1">
                        {['present', 'retard', 'absent'].map(s => (
                            <label key={s} className="flex items-center cursor-pointer bg-gray-50 px-3 py-2 rounded border hover:bg-gray-100">
                                <input type="radio" value={s} checked={status === s} onChange={e => setStatus(e.target.value)} 
                                    className="mr-2 text-blue-600" />
                                <span className="capitalize font-medium text-gray-700">{s === 'present' ? 'Présent' : s === 'retard' ? 'Retard' : 'Absent'}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Heure Arrivée</label>
                        <input type="time" value={arrivalTime} onChange={e => setArrivalTime(e.target.value)} 
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Heure Départ</label>
                        <input type="time" value={departureTime} onChange={e => setDepartureTime(e.target.value)} 
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500" />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observation</label>
                    <textarea rows="2" value={comments} onChange={e => setComments(e.target.value)}
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500" placeholder="Ex: Motif du retard..." />
                </div>

                <button type="submit" disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-2.5 rounded font-medium hover:bg-blue-700 flex justify-center items-center">
                    {isSubmitting ? <span className="animate-spin mr-2">⌛</span> : <Save size={18} className="mr-2"/>} Enregistrer
                </button>
            </form>
          </div>

          {/* Sidebar Liste du jour */}
          <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 h-fit">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center">
                <Calendar size={16} className="mr-2"/> Saisies du {new Date(inputDate).toLocaleDateString()}
            </h4>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {todaysRecords.length === 0 ? <p className="text-gray-500 italic text-sm">Aucune donnée.</p> : 
                 todaysRecords.map(rec => (
                    <div key={rec.id} className="bg-white p-3 rounded shadow-sm text-sm border-l-4 border-blue-400">
                        <div className="font-bold text-gray-800">{rec.teacherName}</div>
                        <div className="flex justify-between mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                                rec.status === 'present' ? 'bg-green-100 text-green-800' : 
                                rec.status === 'retard' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                            }`}>{rec.status.toUpperCase()}</span>
                            <span className="text-gray-500 font-mono">{rec.arrivalTime || '--:--'} - {rec.departureTime || '--:--'}</span>
                        </div>
                    </div>
                 ))}
            </div>
          </div>
        </div>
      )}

      {/* ================= CONTENU ONGLET : RAPPORT ================= */}
      {activeTab === 'report' && (
        <div className="space-y-6">
            {/* Filtres (Non imprimés) */}
            <div className="no-print bg-white p-4 rounded-lg shadow border border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Du</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 border rounded" />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Au</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 border rounded" />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Enseignant</label>
                    <select value={filterTeacher} onChange={e => setFilterTeacher(e.target.value)} className="w-full p-2 border rounded">
                        <option value="">Tous les enseignants</option>
                        {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div className="flex gap-2">
                    <button onClick={handleReportSearch} disabled={isSearching} className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 flex justify-center items-center">
                        {isSearching ? "..." : <Search size={18} />}
                    </button>
                    <button onClick={handleGeneratePrintReport} className="bg-gray-800 text-white py-2 px-4 rounded hover:bg-gray-900">
                        <Printer size={18} />
                    </button>
                </div>
            </div>

            {/* Zone de prévisualisation */}
            <div className="bg-white p-8 rounded-lg shadow">
              <div className="text-center mb-6 pb-4 border-b-2 border-gray-200">
                  <h1 className="text-2xl font-bold uppercase text-gray-800">Prévisualisation du Rapport de Présence Enseignants</h1>
                  <p className="text-gray-600">Période : {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}</p>
              </div>

              {attendanceReportData.length > 0 ? (
                  <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead className="bg-gray-100">
                          <tr>
                              <th className="border p-2 text-left">Date</th>
                              <th className="border p-2 text-left">Nom</th>
                              <th className="border p-2 text-center">Statut</th>
                              <th className="border p-2 text-center">Arrivée</th>
                              <th className="border p-2 text-center">Départ</th>
                              <th className="border p-2 text-left">Observations</th>
                              <th className="border p-2 text-center">Émargement</th> {/* Ajout ici aussi pour la prévisualisation */}
                          </tr>
                      </thead>
                      <tbody>
                          {attendanceReportData.map((row, idx) => {
                               const rowDate = row.date?.toDate ? row.date.toDate() : new Date(row.date);
                               return (
                                  <tr key={idx} className="hover:bg-gray-50">
                                      <td className="border p-2">{rowDate.toLocaleDateString()}</td>
                                      <td className="border p-2 font-medium">{row.teacherName}</td>
                                      <td className="border p-2 text-center">
                                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                              row.status === 'present' ? 'bg-green-100 text-green-800' :
                                              row.status === 'absent' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                          }`}>{row.status.toUpperCase()}</span>
                                      </td>
                                      <td className="border p-2 text-center font-mono">{row.arrivalTime || '-'}</td>
                                      <td className="border p-2 text-center font-mono">{row.departureTime || '-'}</td>
                                      <td className="border p-2 italic text-gray-600">{row.comments}</td>
                                      <td className="border p-2 text-center">---</td> {/* Indication visuelle pour la prévisualisation */}
                                  </tr>
                               );
                          })}
                      </tbody>
                  </table>
              ) : (
                  <p className="text-center text-gray-500 italic p-8">Aucune donnée pour cette période après filtrage.</p>
              )}
            </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAttendanceReport;
